import { GridRowSelectionModel, GridSortItem } from "@mui/x-data-grid";
import CustomDataGridV2 from "../../../../../components/CustomDataGridV2";
// import Flex from "../../../../../components/Flex";
// import { Typography } from "@mui/material";
import { PatientName } from "../../../../../utils/util-components/UtilComponents";
import { formatDateTime } from "../../../../../utils/date-time-utility";
import {
  DGT_Pagination,
  getTrimmedUuid,
  // getValueFromObj,
} from "../../../../../utils/utils";
import {
  getEncounterReportColumns,
  ENCOUNTER_STATUS_MAP,
  ENCOUNTER_MODE_MAP,
} from "./patient-encounter-constants";
import StatusChip from "../../../../../components/StatusChip";
import CodeListCell from "./CodeListCell";
import EncounterIdCell from "./EncounterIdCell";

interface PatientEncounterTableProps {
  rows: any[];
  rowCount: number;
  loading: boolean;
  pagination: DGT_Pagination;
  onPaginationChange: (model: DGT_Pagination) => void;
  gridSorting: GridSortItem[];
  onSortChange: (model: GridSortItem[]) => void;
  rowSelectionModel?: GridRowSelectionModel;
  onRowSelectionModelChange?: (model: GridRowSelectionModel) => void;
}

const renderStatusCell = ({ row }: any) => {
  const statusConfig = ENCOUNTER_STATUS_MAP[row?.status] ?? {
    label: row?.status ?? "-",
    color: "#999",
    bgColor: "#F5F5F5",
  };
  return (
    <StatusChip
      label={statusConfig.label}
      color={statusConfig.color}
      bgColor={statusConfig.bgColor}
    />
  );
};

const renderModeCell = ({ row }: any) => {
  const modeConfig = ENCOUNTER_MODE_MAP[row?.visitType] ?? {
    label: row?.visitType ?? "-",
    color: "#999",
    bgColor: "#F5F5F5",
  };
  return (
    <StatusChip
      label={modeConfig.label}
      color={modeConfig.color}
      bgColor={modeConfig.bgColor}
    />
  );
};

const renderPatientName = ({ row }: any) => (
  <PatientName
    name={row?.patientName}
    patientId={row?.patientUuid ?? ""}
    disableNavigate={!row?.patientUuid}
  />
);

// Visit ID → opens the encounter summary drawer (same as Ready-for-Billing).
const renderEncounterIdCell = ({ row }: any) => (
  <EncounterIdCell encounterId={row?.encounterId} label={row?.encounterUuid} />
);

const renderIcdCell = ({ row }: any) => (
  <CodeListCell codes={row?.icd10Code} title="ICD-10 Codes" />
);

// CPT may arrive as a comma-separated string (`cptCode`) or, like the Billing
// report, as an object map of codes (`cptCodes`). CodeListCell handles both;
// fall back to the map when the string field is empty/absent.
const renderCptCell = ({ row }: any) => (
  <CodeListCell codes={row?.cptCode || row?.cptCodes} title="CPT Codes" />
);

// const renderCptCode = ({ row }: any) => {
//   const firstCode = getValueFromObj(row?.cptCodes);
//   if (!firstCode) return <Typography fontSize={14}>-</Typography>;
//
//   const remaining = Object.keys(row?.cptCodes ?? {}).length - 1;
//
//   return (
//     <Flex gap="4px" alignItems="center" flexWrap="wrap">
//       <StatusChip
//         label={firstCode}
//         bgColor={"#F2F4F7"}
//         color={"#373D41"}
//         sx={{
//           maxWidth: "150px",
//           "& .MuiChip-label": {
//             textAlign: "left",
//           },
//         }}
//       />
//       {remaining > 0 && (
//         <StatusChip
//           label={`+${remaining}`}
//           bgColor={"#F2F4F7"}
//           color={"#373D41"}
//         />
//       )}
//     </Flex>
//   );
// };

const COLUMNS = getEncounterReportColumns(
  renderEncounterIdCell,
  renderStatusCell,
  renderModeCell,
  renderPatientName,
  renderIcdCell,
  renderCptCell
);

const PatientEncounterTable = ({
  rows,
  rowCount,
  loading,
  pagination,
  onPaginationChange,
  gridSorting,
  onSortChange,
  rowSelectionModel,
  onRowSelectionModelChange,
}: PatientEncounterTableProps) => {
  const formattedRows = (rows ?? []).map((row) => ({
    ...row,
    id: row?.encounterId,
    encounterUuid: getTrimmedUuid(row?.encounterId) ?? "",
    dateTime: formatDateTime(row?.encounterDateAndTime) ?? "",
    chiefComplaint: row?.chiefComplaint ?? "",
    icd10Code: row?.icd10Code ?? "",
    location: row?.location ?? "",
    insurance: row?.insurance ?? "",
  }));

  return (
    <>
      <CustomDataGridV2
        rows={formattedRows}
        getRowId={(row) => row?.encounterId}
        columns={COLUMNS}
        rowCount={rowCount ?? 0}
        loading={loading}
        paginationModel={pagination}
        onPaginationModelChange={onPaginationChange}
        sortModel={gridSorting}
        onSortModelChange={onSortChange}
        checkboxSelection
        rowSelectionModel={rowSelectionModel}
        onRowSelectionModelChange={onRowSelectionModelChange}
      />
    </>
  );
};

export default PatientEncounterTable;
